import { useLazyQuery, useQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { Box, Button, Card, Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { Skeleton } from "../../../../components/loadingSkeleton/Skeleton";
import PleaseWait from "../../../../components/PleaseWait";
import { InputNoForm } from "../../../../components/TextInput";
import { GET_ARTICLES_CATEGORIES, GET_ARTICLES_FOR_CATEGORY } from "../../../../graphql/queries";
import { IArticle, IgetArticleCategoriesInput, IGetArticleCategoriesOutput, IGetArticlesInCategoryFromWebInput, IGetArticlesInCategoryFromWebOutput } from "../../../../graphql/types/queries/articles";
import doctorIcon from '../../../../static/doctorIcon.png'

const CategoryTag = styled(Text)`
  transition: 0.3s all ease-in-out;
  text-decoration: underline, 
  cursor: pointer,
  font-weight: 300;
  font-size: 14px;
  color: #F8A294;

  &:hover {
  font-weight: 500;
  }
`;

const BoxRight = styled(Box)`
  width: 254px;

  @media screen and (max-width: 1300px) {
    display: none;
  }
`;

const BoxRightMobile = styled(Flex)`
  flex-direction: column;
  @media screen and (min-width: 68.8125em) {
    display: none;
  }
`;

const Title = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Img = styled(Image)`
  transition: 0.3s all ease-in-out;
  object-fit: cover;
  &:hover {
    transform: scale(1.1);
  }
`;

const ArticleCardContent = styled(Card)`
  margin-right: 24px;
  cursor: pointer;
  &:hover ${Img} {
    transform: scale(1.1);
  }
  @media screen and (max-width: 480px) {
    &.individual {
      margin-right: 0;
    }
  }
`;

const RecipeCardContent = styled(Card)`
  cursor: pointer;
  margin-right: 24px;
  &:hover ${Img} {
    transform: scale(1.15);
  }
  @media screen and (max-width: 480px) {
    &.individual {
      margin-right: 0px;
    }
  }
`;

const CategoryTagMobile = styled(Box)`
  transition: 0.3s all ease-in-out;
  border-radius: 24px;
  padding: 4px 8px;
  color: #7992b3;
  border: 1px solid #7992b3;
  margin-right: 18px;
  white-space: nowrap;
  cursor: pointer;
  font-weight: 300;

  &.isSelected {
    font-weight: 500;
    color: rgb(53, 78, 121);
    border: 1px solid rgb(53, 78, 121);
  }
`;

export const RecipeCardLoading = ({ className }: { className: string }) => {
  return (
    <RecipeCardContent className={className} style={{ minWidth: '150px', width: '150px', height: '208px', marginRight: '24px', overflow: 'hidden', marginBottom: '32px', borderRadius: '10px' }}>
      <Flex flexDirection={'column'} style={{ position: 'relative', height: '100%', paddingTop: '30px' }}>
        <PleaseWait
          text={' '}
        />
        <Flex style={{ height: '50%', left: 0, right: 0, bottom: 0, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', position: 'absolute', padding: '6px' }}>
          <Skeleton width="75px" height="16px" />
        </Flex>
      </Flex>
    </RecipeCardContent>
  );
}

export const ArticleCard = ({ article, onClick, className, isSelected, onSelect, canSelect = true }: { canSelect?: boolean, article: IArticle, onClick: () => void, className?: string, isSelected: boolean, onSelect: () => void }) => {
  return (
    <ArticleCardContent className={className} onClick={onClick} style={{ minWidth: '200px', width: '200px', height: '240px', overflow: 'hidden', marginBottom: '32px', borderRadius: '10px' }}>
      <Flex flexDirection={'column'} style={{ position: 'relative', height: '100%' }}>
        <Img src={`${article.thumbnailUrl}`} style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
        <Flex style={{ height: '84px', left: 0, right: 0, bottom: 0, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', position: 'absolute', padding: '6px' }}>
          <Title textAlign={'center'}>{article.title}</Title>
        </Flex>
        {!!canSelect && (
          <Flex onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onSelect()
          }} style={{ paddingBottom: '8px', position: 'absolute', right: 0, top: 0, width: '80px', height: '55px', justifyContent: 'center', alignItems: 'flex-end', transformOrigin: 'bottom right', transform: 'rotate(45deg)', background: !isSelected ? 'white' : '#344F7A', boxShadow: '0px 4px 20px rgb(0 0 0 / 33%)' }}>
            {isSelected ? (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C3.67392 0 2.40215 0.526784 1.46447 1.46447C0.526784 2.40215 0 3.67392 0 5V15C0 16.3261 0.526784 17.5979 1.46447 18.5355C2.40215 19.4732 3.67392 20 5 20H15C16.3261 20 17.5979 19.4732 18.5355 18.5355C19.4732 17.5979 20 16.3261 20 15V5C20 3.67392 19.4732 2.40215 18.5355 1.46447C17.5979 0.526784 16.3261 0 15 0H5ZM13.73 8.684C13.8198 8.58814 13.8899 8.47552 13.9362 8.35257C13.9825 8.22963 14.0041 8.09878 13.9998 7.96747C13.9956 7.83617 13.9655 7.707 13.9113 7.58732C13.8571 7.46765 13.7799 7.35982 13.684 7.27C13.5881 7.18018 13.4755 7.11011 13.3526 7.06381C13.2296 7.01751 13.0988 6.99588 12.9675 7.00015C12.8362 7.00442 12.707 7.03451 12.5873 7.08871C12.4677 7.1429 12.3598 7.22014 12.27 7.316L9.187 10.606L7.664 9.253C7.46446 9.08712 7.20825 9.0053 6.9495 9.02482C6.69075 9.04434 6.44972 9.16368 6.27732 9.35762C6.10493 9.55156 6.01467 9.80492 6.02562 10.0642C6.03657 10.3234 6.14787 10.5683 6.336 10.747L8.586 12.747C8.78069 12.9199 9.03515 13.0101 9.29529 12.9983C9.55543 12.9866 9.80072 12.8738 9.979 12.684L13.729 8.684H13.73Z" fill="white" />
            </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 3H7C4.79086 3 3 4.79086 3 7V17C3 19.2091 4.79086 21 7 21H17C19.2091 21 21 19.2091 21 17V7C21 4.79086 19.2091 3 17 3Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            )}
          </Flex>)}
      </Flex>
    </ArticleCardContent>
  );
};

export const ArticleCardLoading = ({ className }: { className?: string }) => {
  return (
    <ArticleCardContent className={className} style={{ minWidth: '200px', width: '200px', height: '240px', overflow: 'hidden', marginBottom: '32px', borderRadius: '10px' }}>
      <Flex flexDirection={'column'} style={{ position: 'relative', height: '100%', paddingTop: '70px' }}>
        <PleaseWait
          text={' '}
        />
        <Flex style={{ height: '84px', left: 0, right: 0, bottom: 0, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', position: 'absolute', padding: '6px' }}>
          <Skeleton width="75px" height="16px" />
        </Flex>
      </Flex>
    </ArticleCardContent>
  );
};
const Articles = ({ selectedArticles, setSelectedArticles, viewArticle, canSelect = true }: { canSelect?: boolean, selectedArticles: Array<IArticle | null>, setSelectedArticles: (recipe: IArticle) => void, viewArticle: (article: IArticle) => void }) => {
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [filter, setFilter] = useState(null);
  const [filterTmp, setFilterTmp] = useState(null);
  const containerRef = useRef<any>(null);
  const { data: dataCats, loading: loadingCats } = useQuery<IGetArticleCategoriesOutput, IgetArticleCategoriesInput>(GET_ARTICLES_CATEGORIES)
  const [query, { data, loading, fetchMore }] = useLazyQuery<IGetArticlesInCategoryFromWebOutput, IGetArticlesInCategoryFromWebInput>(GET_ARTICLES_FOR_CATEGORY, {
    variables: {
      categoryId: selectedCategory,
      filter,
      idsToExclude: [],
      take: 24
    }
  })

  useEffect(() => {
    query({
      variables: {
        categoryId: selectedCategory,
        filter,
        idsToExclude: [],
        take: 24
      }
    })
  }, [])

  useEffect(() => {
    if (containerRef && containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
    query({
      variables: {
        categoryId: selectedCategory,
        filter,
        idsToExclude: [],
        take: 24
      }
    })
  }, [filter, selectedCategory])

  const onScroll = (e: any) => {
    if (!!data && data.getArticlesInCategoryFromWeb && !isFetchingMore) {
      if (e.target.scrollTop + e.target.clientHeight >= e.target.scrollHeight) {
        if (data.getArticlesInCategoryFromWeb.length % 24 !== 0) return;
        setTimeout(() => {
          onFetchMore();
        }, 300);

        return;
      }
    }
  };

  const onFetchMore = () => {
    if (!data || !data.getArticlesInCategoryFromWeb || data.getArticlesInCategoryFromWeb.length < 1) return;
    setIsFetchingMore(true);
    fetchMore({
      variables: {
        categoryId: selectedCategory,
        filter,
        idsToExclude: [...data.getArticlesInCategoryFromWeb.filter(x => !!x).map((x) => x!.id)],
        take: 24
      },
      updateQuery: (previousResult: any, { fetchMoreResult }) => {
        setIsFetchingMore(false);
        if (!fetchMoreResult || !fetchMoreResult.getArticlesInCategoryFromWeb || fetchMoreResult.getArticlesInCategoryFromWeb.length < 1) {
          return previousResult;
        }
        return Object.assign({}, previousResult, {
          getArticlesInCategoryFromWeb: [...previousResult.getArticlesInCategoryFromWeb, ...fetchMoreResult.getArticlesInCategoryFromWeb],
        });
      },
    });
  };
  return (
    <>
    <BoxRightMobile  paddingX={['20px',0,0]}>
            <InputNoForm
              style={{ backgroundColor: 'white', border: '0px', flex: 1, paddingTop: '12px', paddingBottom: '12px' }}
              containerStyle={{ width: '100%' }}
              placeholder='Search'
              type='search'
              onChange={(e: any) => {
                if (!e.target.value) {
                  setFilter(null);
                  setFilterTmp(null);
                  return;
                }
                setFilterTmp(e.target.value);
              }}
              value={filterTmp}
              onReturn={() => {
                setFilter(filterTmp);
              }} />
      <Box style={{ maxWidth: '100vw', overflow: 'auto', width: '100vw', marginLeft: '-20px', marginRight: '-20px', paddingLeft: '20px', paddingRight: '20px' }}>
        <Flex style={{ paddingTop: '24px', paddingBottom: '24px', minWidth: '1200px' }}>
          {!loadingCats ? (
            <>
              {((dataCats || {}).getArticleCategories || [])
                .filter((x) => {
                  return x.name.toLowerCase() !== '#diet' && x.name.toLowerCase() !== '#recipes';
                }).map((x) => {
                return (
                  <CategoryTagMobile
                    className={selectedCategory === x.id ? 'isSelected' : ''}
                    key={x.id}
                    onClick={() => {
                      setSelectedCategory(x.id);
                    }}
                    mb={2}
                  >
                    {x.name}
                  </CategoryTagMobile>
                );
              })}
            </>
          ) : (
            <>
              {[...Array(10)].map((x, i) => {
                return (
                  <CategoryTagMobile>
                    <Skeleton height="14px" width="48px" key={`loadingArtiCat${i}`} />
                  </CategoryTagMobile>
                );
              })}
            </>
          )}
        </Flex>
      </Box>
    </BoxRightMobile>
    <Flex ref={containerRef} flex={1} style={{ overflow: 'auto' }} onScroll={(e) => onScroll(e)}>
      <Flex flex={1} flexWrap={'wrap'} p={'20px'}>
        {!!loading && !isFetchingMore && (!data || !data.getArticlesInCategoryFromWeb) ? (
          <>
            {[...Array(24)].map((x, i) => {
              return <ArticleCardLoading className="individual" key={`loadingArtiCat${i}`} />;
            })}
          </>
        ) : (
          <>
            {((data || {}).getArticlesInCategoryFromWeb || []).map((x) => {
              return (
                <ArticleCard
                canSelect={canSelect}
                  key={x.id}
                  article={x}
                  onSelect={() => setSelectedArticles(x)}
                  className="individual"
                  isSelected={selectedArticles.findIndex(z => z?.id === x.id) > -1}
                  onClick={() => { viewArticle(x) }}
                />
              );
            })}
            {!isFetchingMore && ((data || {}).getArticlesInCategoryFromWeb || []).length === 0 && (
              <Flex style={{ flexDirection: 'column', alignItems: 'center' }}>
                <img src={doctorIcon} style={{ maxWidth: '120px' }} />
                <Text my={3}>We did not find any article that match your search</Text>
                <Button
                  variant={'primary'}
                  onClick={() => {
                    setSelectedCategory(null);
                    setFilter(null);
                    setFilterTmp(null);
                  }}
                >
                  Reset Search
                </Button>
              </Flex>
            )}
          </>
        )}
      </Flex>

      <BoxRight>
        <Flex style={{ position: 'fixed' }} flexDirection={'column'}>
          <Flex flexDirection={'row'} mt={4} style={{
            background: 'white',
            borderRadius: '12px', padding: '0 6px 0 12px', height: '51px',
            border: '1px solid #344F7A'
          }} alignItems='center'
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
              <path d="M21 21L16.514 16.506L21 21ZM19 10.5C19 12.7543 18.1045 14.9163 16.5104 16.5104C14.9163 18.1045 12.7543 19 10.5 19C8.24566 19 6.08365 18.1045 4.48959 16.5104C2.89553 14.9163 2 12.7543 2 10.5C2 8.24566 2.89553 6.08365 4.48959 4.48959C6.08365 2.89553 8.24566 2 10.5 2C12.7543 2 14.9163 2.89553 16.5104 4.48959C18.1045 6.08365 19 8.24566 19 10.5V10.5Z" stroke="#344F7A" stroke-width="2" stroke-linecap="round" />
            </svg>
            <InputNoForm
              style={{ backgroundColor: 'white', border: '0px', flex: 1, paddingTop: '12px', paddingBottom: '12px' }}
              containerStyle={{ width: '100%' }}
              placeholder='Search'
              type='search'
              onChange={(e: any) => {
                if (!e.target.value) {
                  setFilter(null);
                  setFilterTmp(null);
                  return;
                }
                setFilterTmp(e.target.value);
              }}
              value={filterTmp}
              onReturn={() => {
                setFilter(filterTmp);
              }} />

          </Flex>
          {!!filterTmp &&
            filterTmp !== filter && (
              <Text mt={2} style={{ fontSize: '12px', fontWeight: 300 }}>
                Press <span style={{ fontWeight: 500 }}>Enter</span> to search
              </Text>
            )}

          <Text style={{ fontWeight: 500 }} mt={4} mb={3}>
            Categories
          </Text>
          {!loadingCats ? (
            <>
              {((dataCats || {}).getArticleCategories || [])
                .filter((x) => {
                  return x.name.toLowerCase() !== '#diet' && x.name.toLowerCase() !== '#recipes';
                })
                .map((x) => {
                  return (
                    <CategoryTag
                      key={x.id}
                      onClick={() => {
                        setSelectedCategory(x.id);
                      }}
                      mb={2}
                      color="#F8A294"
                      style={{ fontWeight: selectedCategory === x.id ? 600 : '', cursor: 'pointer' }}
                    >
                      {x.name}
                    </CategoryTag>
                  );
                })}
            </>
          ) : (
            <>
              {[...Array(10)].map((x, i) => {
                return <Skeleton height="18px" width="180px" key={`loadingArtiCat${i}`} />;
              })}
            </>
          )}
        </Flex>
      </BoxRight>
    </Flex>
    </>
  )
}

export default Articles